import React, { useMemo, memo } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import SoothCard from "components/base/SoothCard";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { colorArray } from "../data/color.data";
import AccessibleIconButton from "components/base/AccessibleIconButton";

const ShareComparison = memo(function ShareComparison({
  visitorData,
  bookmarksData,
  isLoadingVisitor,
  isLoadingBookmarks,
}) {
  const makePieData = (dataset, maxSlices = 10) => {
    if (!dataset?.publishersPerc?.length) return [];

    const sorted = [...dataset.publishersPerc]
      .filter((p) => p.publisher !== "Other")
      .sort((a, b) => b.percentageInt - a.percentageInt);

    return sorted.map((p) => ({
      id: p.publisherId,
      label: p.publisher,
      value: p.percentageInt,
    }));
  };

  const visitorChartData = useMemo(
    () => makePieData(visitorData),
    [visitorData]
  );
  const bookmarksChartData = useMemo(
    () => makePieData(bookmarksData),
    [bookmarksData]
  );

  const makeColorMap = (a = [], b = []) => {
    const labels = Array.from(
      new Set([...a.map((d) => d.label), ...b.map((d) => d.label)])
    );
    const map = new Map();
    labels.forEach((label, i) => {
      const colorIndex = i % colorArray.length;
      map.set(label, colorArray[colorIndex]);
    });
    return map;
  };

  const colorMap = useMemo(
    () => makeColorMap(visitorChartData, bookmarksChartData),
    [visitorChartData, bookmarksChartData]
  );

  const ShareCard = ({
    title,
    data,
    isLoading,
    changePercent,
    originalData,
  }) => (
    <SoothCard
      sx={{
        backgroundColor: (theme) => theme.palette.secondary.main,
        borderRadius: 2,
        p: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "start",
          justifyContent: "center",
          gap: 0.5,
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ textAlign: "center" }}>
          {title}
        </Typography>
        <AccessibleIconButton
          size="small"
          sx={{ p: 0.5 }}
          title={
            title === "Share of Visitors"
              ? "Percentage of total visitors that came to your content compared to other publishers."
              : "Percentage of total bookmarks that were saved from your content compared to other publishers."
          }
          tooltipProps={{
            placement: "top",
          }}
        >
          <InfoOutlinedIcon fontSize="small" color="primary" />
        </AccessibleIconButton>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <Skeleton variant="circular" width={150} height={150} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", flex: 1 }}>
            <PieChart
              colors={colorArray}
              margin={{ left: 0, right: 0 }}
              series={[
                {
                  data: data.map((d) => ({
                    ...d,
                    color: colorMap.get(d.label),
                  })),
                  innerRadius: 30,
                  outerRadius: 75,
                  paddingAngle: 2,
                  cornerRadius: 5,
                  valueFormatter: (v) => `${v.value}%`,
                },
              ]}
              width={200}
              height={150}
              slotProps={{
                legend: { hidden: true },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Box sx={{ textAlign: "center", flex: 1 }}>
              {originalData?.publishersPerc?.find(
                (p) => p.publisher === "Other"
              )?.percentageInt > 0 ? (
                <>
                  <Typography variant="body2" color="primary.dark">
                    Other Publishers
                  </Typography>
                  <Typography variant="h6" color="primary.dark">
                    {originalData.publishersPerc.find(
                      (p) => p.publisher === "Other"
                    )?.percentageInt || 0}
                    %
                  </Typography>
                </>
              ) : (
                <Box />
              )}
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography variant="body2" color="primary.dark">
                Your Share
              </Typography>
              <Typography variant="h6" color="primary.dark">
                {originalData?.publishersPerc?.find((p) => p.flag === 1)
                  ?.percentageInt || 0}
                %
                {changePercent !== null && changePercent !== undefined && (
                  <Typography
                    component="span"
                    sx={{
                      color:
                        changePercent > 0
                          ? "success.main"
                          : changePercent < 0
                          ? "error.main"
                          : "text.secondary",
                      ml: 1,
                      fontSize: "0.8em",
                    }}
                  >
                    {changePercent > 0 ? "↑" : changePercent < 0 ? "↓" : ""}
                    {Math.abs(changePercent)}%
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </SoothCard>
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { sm: "1fr", md: "1fr 1fr" },
        gap: 2,
      }}
    >
      <ShareCard
        title="Share of Visitors"
        data={visitorChartData}
        isLoading={isLoadingVisitor}
        changePercent={visitorData?.publisherPercChange?.percentageChangeInt}
        originalData={visitorData}
      />
      <ShareCard
        title="Share of Bookmarks"
        data={bookmarksChartData}
        isLoading={isLoadingBookmarks}
        changePercent={bookmarksData?.publisherPercChange?.percentageChangeInt}
        originalData={bookmarksData}
      />
    </Box>
  );
});

export default ShareComparison;
